import { useEntity } from '@/hooks/queries/useEntityQuery';
import { circuitServiceAdapter } from '@/services/adapters/circuitServiceAdapter';
import { Circuit } from '@/types/floorPlanViewer';
import { useMemo } from 'react';

export function useCircuitsStandardized(floorPlanId: string) {
  const { data, isLoading, error, refetch, create, update, delete: deleteCircuit, ...mutations } = useEntity<Circuit>(
    'circuits',
    floorPlanId,
    circuitServiceAdapter
  );

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!data) return { totalCircuits: 0, loadTypeStats: {} };

    const totalCircuits = data.length;
    
    const loadTypeStats = data.reduce((acc, circuit) => {
      acc[circuit.load_type] = (acc[circuit.load_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return { totalCircuits, loadTypeStats };
  }, [data]);

  // Enhanced create function with floor plan assignment
  const createCircuit = async (circuitData: Partial<Circuit>) => {
    const circuitWithFloorPlan = {
      ...circuitData,
      floor_plan_id: floorPlanId,
    };
    return create(circuitWithFloorPlan);
  };

  // Enhanced update function
  const updateCircuit = async (id: string, updates: Partial<Circuit>) => {
    return update({ id, updates });
  };

  // Bulk operations
  const updateCircuitNames = async (updates: { id: string; name: string }[]) => {
    const promises = updates.map(({ id, name }) => 
      updateCircuit(id, { name })
    );
    return Promise.all(promises);
  };

  return {
    // Data
    circuits: data || [],
    
    // Loading states
    isLoading,
    error,
    
    // Mutation states
    isCreating: mutations.isCreating,
    isUpdating: mutations.isUpdating,
    isDeleting: mutations.isDeleting,
    
    // Operations
    createCircuit,
    updateCircuit,
    deleteCircuit,
    refetch,
    
    // Bulk operations
    updateCircuitNames,
    
    // Statistics
    totalCircuits: statistics.totalCircuits,
    getLoadTypeStats: () => statistics.loadTypeStats,
  };
} 